import { createAsyncThunk, createSlice, type PayloadAction } from '@reduxjs/toolkit';
import { API } from '../../../api';

// {
//   "_id": "6413ed0b1198b8887494cb94",
//   "authId": "org_FTqKCy3FwwD4DWCn",
//   "name": "Starventure",
//   "oid": "starventure",
//   "orgLogo": "https://dosenv2storage.blob.core.windows.net/organizationprofilepicture/1684478846320_Dosenv1.jpg",
//   "description": "Actually it is starventure organization for testing renamed to test organization",
//   "dataLocation": "us",
//   "createdAt": 1684412855,
//   "createdBy": "maneesha@dosen.io",
//   "updatedAt": 1679291587,
//   "updatedBy": "maneesha@dosen.io",
//   "grpId": "64491e3bf529fcb3539746c7",
//   "orgType": "Government & Development Organisation"
// }

export interface IOrgData {
  _id: string;
  authId: string;
  name: string;
  orgLogo: string;
  description: string;
  orgType: string;
  userTypes: string[];
  oid: string;
  grpId: string;
  currency: any;
  displayRoles?: {
    admin: string[];
    mentee: string[];
    mentor: string[];
  };
  orgSettings: boolean;
  /**
   * The org's attribute lists. `isTeamsList` marks the Teams list (the
   * BE also takes a list named "Teams" when none is marked).
   */
  memberAttributes?: Array<{
    _id: string;
    name: string;
    isTeamsList?: boolean;
    values?: Array<{ _id: string; value: string }>;
  }>;
  /**
   * Per-org switches as the BE stores them. The product feature flags are GA
   * (enabled everywhere, no longer read); what is still consulted here is
   * `editingFrozen` (relaunch editing freeze) and `courseVersioning`.
   */
  flags?: {
    /** RDP relaunch editing freeze — every admin editing surface goes read-only. */
    editingFrozen?: boolean;
    /** Course versioning (Designer draft, Version History, Publish update). Opt-in per org. */
    courseVersioning?: boolean;
    /** Hiring (DA-5042): the sandboxed Hiring section and its candidate
     *  assessments. Off by default, and a compliance switch rather than a
     *  preview toggle: an org gets a feature that assesses job candidates with
     *  AI on purpose. The backend reads it too (assertHiringEnabled). */
    hiring?: boolean;
    [key: string]: boolean | undefined;
  };
}

interface IInitialState {
  loading: boolean;
  data: IOrgData | undefined;
  error: boolean;
  errorText: string;
}

const initialState: IInitialState = {
  loading: false,
  data: undefined,
  error: false,
  errorText: ''
};

export const fetchOrgDetails = createAsyncThunk('org/name', (orgId: string) => {
  return API.getOrgName(orgId).then((response) => response.data);
});

const orgDetailsSlice = createSlice({
  name: 'orgDetails',
  initialState,
  reducers: {
    updateOrgSettings: (state, action) => {
      // Preserve flags (e.g. editingFrozen, courseVersioning) that arrive with
      // fetchOrgDetails but are not returned by the org-settings PATCH endpoint.
      // Without this, saving from Org Settings wipes them until the next fetch.
      // memberAttributes likewise, when the payload leaves them out.
      return {
        ...state,
        data: {
          ...action.payload,
          flags: state.data?.flags,
          memberAttributes: action.payload?.memberAttributes ?? state.data?.memberAttributes
        }
      };
    },
    // Only the attribute lists change (Audience attributes saved, or the Teams
    // list marked), so the other Org Settings cards keep their unsaved edits.
    updateOrgMemberAttributes: (state, action: PayloadAction<IOrgData['memberAttributes']>) => {
      if (state.data) state.data.memberAttributes = action.payload;
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchOrgDetails.pending, (state) => {
        state.loading = true;
        state.error = false;
        state.errorText = '';
      })
      .addCase(fetchOrgDetails.fulfilled, (state, action) => {
        state.loading = false;
        // The product feature flags are GA (every org, old and new) and nothing
        // reads them any more, so the payload is stored as it arrives. `flags`
        // still passes through untouched: `editingFrozen` and `courseVersioning`
        // are real per-org switches and are read from here.
        state.data = action.payload;
        state.error = false;
        state.errorText = '';
      })
      .addCase(fetchOrgDetails.rejected, (state, action) => {
        state.loading = false;
        state.data = undefined;
        state.error = true;
        state.errorText = action.error.message || '';
      });
  }
});

export default orgDetailsSlice.reducer;
export const orgSettingsActions = orgDetailsSlice.actions;
